import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isObject, values, trim } from 'lodash'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import apiGet from 'lib/network/apiGet'
import apiPost from 'lib/network/apiPost'

const ACCOUNT_TYPES = ['user', 'company', 'partner']
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const AddUser = ({ onSuccess, close }) => {
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [setPasswordByEmail, setSetPasswordByEmail] = useState(false)
  const [accountType, setAccountType] = useState('user')
  const [company, setCompany] = useState('')
  const [branch, setBranch] = useState([])
  const [partner, setPartner] = useState('')
  const [companyNames, setCompanyNames] = useState([])
  const [partnerNames, setPartnerNames] = useState([])
  const [branches, setBranches] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    fetchCompanies()
    fetchPartners()
  }, [])

  const fetchCompanies = async () => {
    const response = await apiGet('companies', { pageSize: 1000 })
    if (response?.data?.company) {
      setCompanyNames(
        response.data.company.map(item => ({
          id: item._id,
          name: item.name,
        }))
      )
    } else {
      setCompanyNames([])
    }
  }

  const fetchPartners = async () => {
    try {
      const response = await apiGet('partners', { pageSize: 1000 })
      if (response?.data?.partners) {
        setPartnerNames(
          response.data.partners.map(item => ({
            id: item._id,
            name: item.name,
          }))
        )
      } else {
        setPartnerNames([])
      }
    } catch (error) {
      setPartnerNames([])
    }
  }

  const fetchBranches = async companyId => {
    try {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        setBranches(
          response.data.map(item => ({
            id: item._id,
            name: item.name,
          }))
        )
      } else {
        setBranches([])
      }
    } catch (error) {
      setBranches([])
    }
  }

  const handleAccountTypeChange = type => {
    setAccountType(type)
    setCompany('')
    setBranch([])
    setPartner('')
    setBranches([])
  }

  const handleSetPasswordByEmailChange = e => {
    const checked = e.target.checked
    setSetPasswordByEmail(checked)
    if (checked) {
      setPassword('')
    }
  }

  const handleAddUser = async () => {
    if (isSubmitting) return
    const trimmedEmail = trim(email)
    if (!trimmedEmail) {
      notify.error(t('addUserModal.messages.emailRequired'))
      return
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      notify.error(t('addUserModal.messages.emailInvalid'))
      return
    }
    if (!setPasswordByEmail && !password) {
      notify.error(t('addUserModal.messages.passwordRequired'))
      return
    }
    if (accountType === 'company' && (!company || !branch?.length)) {
      notify.error(t('addUserModal.messages.companyBranchRequired'))
      return
    }
    if (accountType === 'partner' && !partner) {
      notify.error(t('addUserModal.messages.partnerRequired'))
      return
    }

    setIsSubmitting(true)
    try {
      const response = await apiPost(
        'users/add',
        {
          email: trimmedEmail,
          accountType,
          ...(setPasswordByEmail ? { setPasswordByEmail: true } : { password }),
          ...(accountType === 'company' ? { company, branch } : {}),
          ...(accountType === 'partner' ? { partner } : {}),
        },
        0,
        false
      )
      if (response.success) {
        notify.success(setPasswordByEmail ? t('addUserModal.messages.userAddedWithEmailSetup') : t('addUserModal.messages.userAddedSuccessfully'))
        onSuccess(response.data._id)
      } else {
        const fieldErrors = response.err && isObject(response.err) ? values(response.err).filter(Boolean).join(', ') : null
        notify.error(fieldErrors || response.message || t('addUserModal.messages.failedToAddUser'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label className="block text-sm font-normal text-gray-700 mb-1">{t('addUserModal.form.accountType')}</label>
        <div className="mt-1 sm:mt-0 sm:col-span-2 flex flex-wrap gap-4">
          {ACCOUNT_TYPES.map(type => (
            <label key={type} className="inline-flex items-center text-sm text-gray-700">
              <input
                type="radio"
                name="accountType"
                value={type}
                checked={accountType === type}
                onChange={() => handleAccountTypeChange(type)}
                className="focus:ring-dark-500 h-4 w-4 text-dark-600 border-gray-300"
              />
              <span className="ml-2">{t(`addUserModal.form.accountTypes.${type}`)}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label htmlFor="email" className="block text-sm font-normal text-gray-700 mb-1">
          {t('addUserModal.form.emailAddress')}
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2">
          <input
            type="email"
            name="email"
            id="emaidfsdsl"
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
          />
        </div>
      </div>

      {accountType === 'company' && (
        <>
          <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
            <label htmlFor="company" className="block text-sm font-normal text-gray-700 mb-1">
              {t('addUserModal.form.company')}
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <Selection
                onChange={value => {
                  if (value?.id) {
                    setCompany(value.id)
                    setBranch([])
                    fetchBranches(value.id)
                  }
                }}
                title={t('addUserModal.placeholders.selectCompany')}
                list={companyNames}
                value={company || ''}
                className="w-full"
              />
            </div>
          </div>
          <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
            <label htmlFor="branch" className="block text-sm font-normal text-gray-700 mb-1">
              {t('addUserModal.form.branch')}
            </label>
            <div className="mt-1 sm:mt-0 sm:col-span-2">
              <Selection
                onChange={branchIds => setBranch(branchIds || [])}
                multiple
                title={t('addUserModal.placeholders.selectBranch')}
                list={branches}
                value={branch}
                className="w-full"
              />
            </div>
          </div>
        </>
      )}

      {accountType === 'partner' && (
        <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
          <label htmlFor="partner" className="block text-sm font-normal text-gray-700 mb-1">
            {t('addUserModal.form.partner')}
          </label>
          <div className="mt-1 sm:mt-0 sm:col-span-2">
            <Selection
              onChange={value => {
                if (value?.id) {
                  setPartner(value.id)
                }
              }}
              title={t('addUserModal.placeholders.selectPartner')}
              list={partnerNames}
              value={partner || ''}
              className="w-full"
            />
          </div>
        </div>
      )}

      <div className="mb-4 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start">
        <label htmlFor="password" className="block text-sm font-normal text-gray-700 mb-1">
          {t('addUserModal.form.password')}
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2">
          <input
            type="password"
            name="password"
            id="password"
            value={password}
            disabled={setPasswordByEmail}
            onChange={e => setPassword(e.target.value)}
            className="block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={setPasswordByEmail} onChange={handleSetPasswordByEmailChange} className="rounded border-gray-300 text-dark-600 focus:ring-dark-500" />
            {t('addUserModal.form.setPasswordByEmail')}
          </label>
        </div>
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={isSubmitting}
          className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:ml-3 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handleAddUser}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>{t('addUserModal.buttons.pleaseWait')}</span>
            </span>
          ) : (
            t('addUserModal.buttons.addUser')
          )}
        </button>
        <button
          type="button"
          disabled={isSubmitting}
          className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => close()}
        >
          {t('addUserModal.buttons.close')}
        </button>
      </div>
    </div>
  )
}

export default AddUser
